<template>
  <div class="gift-list">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>礼物管理</span>
          <el-button type="primary" @click="handleAdd">添加礼物</el-button>
        </div>
      </template>
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="礼物名称">
          <el-input v-model="searchForm.name" placeholder="请输入礼物名称" clearable></el-input>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
        </el-form-item>
      </el-form>
      <el-table :data="giftList" style="width: 100%">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="礼物名称" min-width="160"></el-table-column>
        <el-table-column prop="icon" label="图标" width="100">
          <template #default="scope">
            <el-image
              v-if="scope.row.icon || scope.row.image"
              :src="scope.row.icon || scope.row.image"
              class="gift-thumb"
              fit="cover"
              :preview-src-list="[scope.row.icon || scope.row.image]"
            ></el-image>
            <span v-else class="gift-thumb--empty">无</span>
          </template>
        </el-table-column>
        <el-table-column prop="effect_file" label="特效文件" width="120">
          <template #default="scope">
            <el-link
              v-if="scope.row.effect_file"
              type="primary"
              :underline="false"
              :href="scope.row.effect_file"
              target="_blank"
            >查看文件</el-link>
            <span v-else class="gift-thumb--empty">无</span>
          </template>
        </el-table-column>
        <el-table-column prop="price" label="价格" width="100">
          <template #default="scope">
            <span class="gift-price">¥{{ Number(scope.row.price || 0).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="80"></el-table-column>
        <el-table-column prop="is_enabled" label="启用状态" width="100">
          <template #default="scope">
            <el-switch
              :value="scope.row.is_enabled === 1"
              @change="handleToggleEnabled(scope.row)"
            ></el-switch>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150">
          <template #default="scope">
            <el-button size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(scope.row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        @current-change="handleCurrentChange"
        @size-change="handleSizeChange"
      />
    </el-card>

    <!-- 添加/编辑礼物对话框 -->
    <el-dialog :visible.sync="dialogVisible" :title="dialogTitle" width="560px" @close="resetForm">
      <el-form ref="formRef" :model="form" :rules="formRules" label-width="110px">
        <el-form-item label="礼物名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入礼物名称" maxlength="255"></el-input>
        </el-form-item>
        <el-form-item label="礼物图片" prop="image">
          <el-upload
            class="gift-uploader"
            action="/api/admin/upload/image"
            :headers="uploadHeaders"
            :data="{ dir: 'gift' }"
            name="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            :show-file-list="false"
            :before-upload="beforeImageUpload"
            :on-success="handleImageSuccess"
            :on-error="handleUploadError"
          >
            <img v-if="form.image" :src="form.image" class="gift-uploader__preview">
            <div v-else class="gift-uploader__placeholder" v-loading="imageUploading">
              <i class="el-icon-plus"></i>
              <span>上传图片</span>
            </div>
          </el-upload>
          <el-button
            v-if="form.image"
            type="text"
            class="gift-uploader__remove"
            @click="form.image = ''"
          >移除</el-button>
          <div class="gift-uploader__tip">JPG/PNG/GIF/WEBP，不超过5MB</div>
        </el-form-item>
        <el-form-item label="特效文件">
          <el-upload
            class="effect-uploader"
            action="/api/admin/upload/file"
            :headers="uploadHeaders"
            :data="{ dir: 'gift-effect' }"
            name="file"
            accept=".svga,.mp4,.json,.gif,.zip"
            :show-file-list="false"
            :before-upload="beforeEffectUpload"
            :on-success="handleEffectSuccess"
            :on-error="handleUploadError"
          >
            <el-button type="primary" plain :loading="effectUploading">
              {{ effectUploading ? '上传中...' : (form.effect_file ? '重新上传' : '上传特效文件') }}
            </el-button>
            <el-link
              v-if="form.effect_file"
              type="success"
              :underline="false"
              :href="form.effect_file"
              target="_blank"
              class="effect-uploader__link"
            >已上传：{{ effectFileName }}</el-link>
            <el-button
              v-if="form.effect_file"
              type="text"
              class="gift-uploader__remove"
              @click="form.effect_file = ''"
            >移除</el-button>
          </el-upload>
          <div class="gift-uploader__tip">支持 SVGA/MP4/JSON(Lottie)/GIF/ZIP，不超过20MB</div>
        </el-form-item>
        <el-form-item label="价格" prop="price">
          <el-input-number v-model="form.price" :min="0" :precision="2" :step="1" controls-position="right"></el-input-number>
          <span class="form-tip">单位：元</span>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" :max="9999" controls-position="right"></el-input-number>
          <span class="form-tip">数值越小越靠前</span>
        </el-form-item>
        <el-form-item label="启用状态">
          <el-switch
            :value="form.is_enabled === 1"
            @change="(val) => form.is_enabled = val ? 1 : 0"
          ></el-switch>
          <span class="form-tip">禁用后前端不可选用该礼物</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'GiftList',
  data() {
    return {
      giftList: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      searchForm: { name: '' },
      dialogVisible: false,
      dialogTitle: '添加礼物',
      submitting: false,
      imageUploading: false,
      effectUploading: false,
      form: {
        id: null,
        name: '',
        image: '',
        icon: '',
        effect_file: '',
        price: 0,
        sort: 0,
        is_enabled: 1
      },
      formRules: {
        name: [{ required: true, message: '请输入礼物名称', trigger: 'blur' }],
        image: [{ required: true, message: '请上传礼物图片', trigger: 'change' }],
        price: [{ required: true, message: '请输入价格', trigger: 'blur' }]
      }
    }
  },
  computed: {
    // 上传请求头：携带管理员token
    uploadHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`
      }
    },
    // 特效文件名（从URL中提取）
    effectFileName() {
      if (!this.form.effect_file) return ''
      try {
        return decodeURIComponent(this.form.effect_file.split('/').pop())
      } catch (e) {
        return this.form.effect_file
      }
    }
  },
  mounted() {
    this.fetchGiftList()
  },
  methods: {
    // 图片上传前校验：类型与大小（JPG/PNG/GIF/WEBP，5MB）
    beforeImageUpload(file) {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!allowedTypes.includes(file.type)) {
        this.$message.error('只支持JPG、PNG、GIF、WEBP格式的图片')
        return false
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过5MB')
        return false
      }
      this.imageUploading = true
      return true
    },

    // 特效文件上传前校验：扩展名与大小（20MB）
    beforeEffectUpload(file) {
      const allowedExt = ['.svga', '.mp4', '.json', '.gif', '.zip']
      const fileName = file.name.toLowerCase()
      const matched = allowedExt.some(ext => fileName.endsWith(ext))
      if (!matched) {
        this.$message.error('只支持 SVGA/MP4/JSON/GIF/ZIP 格式的特效文件')
        return false
      }
      if (file.size > 20 * 1024 * 1024) {
        this.$message.error('特效文件大小不能超过20MB')
        return false
      }
      this.effectUploading = true
      return true
    },

    // 图片上传成功：取返回的URL填入表单
    handleImageSuccess(res) {
      this.imageUploading = false
      if (res.code === 0 && res.data && res.data.url) {
        this.form.image = res.data.url
        this.$message.success('图片上传成功')
        // 手动触发校验清除红字
        this.$nextTick(() => {
          if (this.$refs.formRef) {
            this.$refs.formRef.validateField('image')
          }
        })
      } else {
        this.$message.error(res.message || '图片上传失败')
      }
    },

    // 特效文件上传成功
    handleEffectSuccess(res) {
      this.effectUploading = false
      if (res.code === 0 && res.data && res.data.url) {
        this.form.effect_file = res.data.url
        this.$message.success('特效文件上传成功')
      } else {
        this.$message.error(res.message || '特效文件上传失败')
      }
    },

    // 上传失败统一处理
    handleUploadError() {
      this.imageUploading = false
      this.effectUploading = false
      this.$message.error('上传失败，请检查网络后重试')
    },

    // 获取礼物列表
    fetchGiftList() {
      const token = localStorage.getItem('adminToken')
      fetch(`/api/admin/gift/list?page=${this.currentPage}&pageSize=${this.pageSize}&name=${encodeURIComponent(this.searchForm.name)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => response.json())
        .then(data => {
          if (data.code === 0) {
            this.giftList = data.data.list
            this.total = data.data.total
          } else {
            this.$message.error(data.message || '获取礼物列表失败')
          }
        })
        .catch(() => {
          this.$message.error('获取礼物列表失败')
        })
    },

    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.fetchGiftList()
    },

    // 分页
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchGiftList()
    },

    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchGiftList()
    },

    // 添加礼物
    handleAdd() {
      this.dialogTitle = '添加礼物'
      this.form = {
        id: null,
        name: '',
        image: '',
        icon: '',
        effect_file: '',
        price: 0,
        sort: 0,
        is_enabled: 1
      }
      this.dialogVisible = true
    },

    // 编辑礼物
    handleEdit(row) {
      this.dialogTitle = '编辑礼物'
      this.form = {
        id: row.id,
        name: row.name,
        image: row.image || '',
        icon: row.icon || '',
        effect_file: row.effect_file || '',
        price: Number(row.price || 0),
        sort: row.sort,
        is_enabled: row.is_enabled
      }
      this.dialogVisible = true
    },

    // 关闭弹窗时清除校验
    resetForm() {
      if (this.$refs.formRef) {
        this.$refs.formRef.clearValidate()
      }
    },

    // 提交表单（新增/更新）
    handleSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return
        this.submitting = true
        const token = localStorage.getItem('adminToken')
        const url = this.form.id ? '/api/admin/gift/update' : '/api/admin/gift/create'
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(this.form)
        })
          .then(response => response.json())
          .then(data => {
            if (data.code === 0) {
              this.$message.success(this.form.id ? '更新成功' : '添加成功')
              this.dialogVisible = false
              this.fetchGiftList()
            } else {
              this.$message.error(data.message || '操作失败')
            }
          })
          .catch(() => {
            this.$message.error('操作失败')
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },

    // 删除礼物（逻辑删除）
    handleDelete(id) {
      this.$confirm('确定要删除该礼物吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const token = localStorage.getItem('adminToken')
        fetch('/api/admin/gift/delete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ id })
        })
          .then(response => response.json())
          .then(data => {
            if (data.code === 0) {
              this.$message.success('删除成功')
              this.fetchGiftList()
            } else {
              this.$message.error(data.message || '删除失败')
            }
          })
          .catch(() => {
            this.$message.error('删除失败')
          })
      }).catch(() => {
        // 用户取消删除
      })
    },

    // 切换启用状态
    handleToggleEnabled(row) {
      const newEnabled = row.is_enabled === 1 ? 0 : 1
      const token = localStorage.getItem('adminToken')
      fetch('/api/admin/gift/toggle-enabled', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id: row.id, is_enabled: newEnabled })
      })
        .then(response => response.json())
        .then(data => {
          if (data.code === 0) {
            row.is_enabled = newEnabled
            this.$message.success(newEnabled === 1 ? '已启用' : '已禁用')
          } else {
            this.$message.error(data.message || '操作失败')
          }
        })
        .catch(() => {
          this.$message.error('操作失败')
        })
    },

    // 格式化日期（时间戳为秒级）
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.gift-list {
  width: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
}

.form-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

/* 列表缩略图（点击可预览大图） */
.gift-thumb {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  cursor: pointer;
  display: block;
}

.gift-thumb--empty {
  color: #c0c4cc;
  font-size: 12px;
}

.gift-price {
  color: #f56c6c;
  font-weight: bold;
}

/* 弹窗内图片上传 */
.gift-uploader {
  display: inline-block;
  vertical-align: top;
}

.gift-uploader__preview,
.gift-uploader__placeholder {
  width: 148px;
  height: 148px;
  border-radius: 6px;
}

.gift-uploader__preview {
  object-fit: cover;
  border: 1px solid #dcdfe6;
  display: block;
}

.gift-uploader__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px dashed #d9d9d9;
  background: #fbfdff;
  color: #8c939d;
  cursor: pointer;
  transition: border-color 0.2s;
}

.gift-uploader__placeholder:hover {
  border-color: #409eff;
  color: #409eff;
}

.gift-uploader__placeholder i {
  font-size: 28px;
}

.gift-uploader__placeholder span {
  font-size: 12px;
}

.gift-uploader__remove {
  margin-left: 10px;
}

.gift-uploader__tip {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

/* 特效文件上传区 */
.effect-uploader {
  display: inline-block;
}

.effect-uploader__link {
  margin-left: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
